import React from 'react';
import PropTypes from 'prop-types';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ReactComponent as ArrowForwardIcon } from 'assets/svg/ArrowForward.svg';
import UndisclosedSalesWithConnect from 'js/components/common/undisclosedSales/UndisclosedSalesWithConnect';
import SearchResultsFunctions from 'js/helpers/search/SearchResultsFunctions';
import { trackEvent } from 'js/actions/segment';
import Segment from 'js/helpers/Segment';
import 'css/crux/components/property/timeline/salesHistoryModal.scss';

const SalesHistoryModal = ({
    isOpen,
    onClose,
    anchorEl,
    salesList = [],
    propertyId,
    addressState,
    undisclosedSalesAccess,
    undisclosedSalesFeature,
    getTransactionType,
    page,
    searchType,
    searchTab,
    resultView,
}) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const sortedSalesList = React.useMemo(() => {
        if (!salesList || salesList.length === 0) return [];
        
        return [...salesList].sort((a, b) => {
            const dateA = new Date(a.saleContractDate || a.contractDate);
            const dateB = new Date(b.saleContractDate || b.contractDate);
            return dateB - dateA; // Descending order (newest first)
        });
    }, [salesList]);

    const getSaleDate = (sale) => {
        const contractDate = sale.contractDate || sale.saleContractDate;
        const settlementDate = sale.settlementDate || sale.saleSettlementDate;
        
        return SearchResultsFunctions.displayRapidSaleDate(
            contractDate,
            settlementDate,
            addressState
        );
    };

    const getSaleType = (sale) => {
        const saleSource = sale.saleSourceType || sale.saleSource || '';
        const isAgentsAdvice = sale.isAgentsAdvice || 
            SearchResultsFunctions.isRapidAgentAdvice(saleSource);
        
        const saleType = sale.saleTypeDescription || sale.type || '';
        
        return SearchResultsFunctions.displayRapidSaleType(
            saleType,
            isAgentsAdvice
        );
    };

    const handleViewFullHistory = () => {
        dispatch(trackEvent(Segment.viewFullSaleHistory({
            page,
            searchType,
            searchTab,
            propertyId,
            resultView,
        })));

        onClose();
        history.push({
            pathname: `/property/${propertyId}`,
            state: { openSaleHistoryModal: true }
        });
    };

    return (
        <Popover
            open={isOpen}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            className="sales-history-modal"
        >
            <Box className="sales-history-header">
                <h2>Sale History</h2>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    className="close-button"
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            
            <Box className="sales-history-content">
                <Table className="sales-table" size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                Sale Date
                            </TableCell>
                            <TableCell>
                                Sale Price
                            </TableCell>
                            <TableCell>
                                Sale Type
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedSalesList && sortedSalesList.length > 0 ? (
                            sortedSalesList.map((sale, index) => {
                                const saleSource = sale.saleSourceType || sale.saleSource || '';
                                const isAgentsAdvice = sale.isAgentsAdvice || 
                                    SearchResultsFunctions.isRapidAgentAdvice(saleSource);
                                
                                const salePrice = sale.saleSoldPrice;
                                
                                return (
                                    <TableRow key={index}>
                                        <TableCell>
                                            {getSaleDate(sale)}
                                        </TableCell>
                                        <TableCell>
                                            <UndisclosedSalesWithConnect
                                                id={`${propertyId}-${index}`}
                                                salePrice={salePrice}
                                                isPriceWithheld={sale.isPriceWithheld}
                                                isAgentsAdvice={isAgentsAdvice}
                                                isExactPrice
                                                containerName="sales-history-modal"
                                                undisclosedSalesAccess={undisclosedSalesAccess}
                                                undisclosedSalesFeature={undisclosedSalesFeature}
                                                getTransactionType={getTransactionType}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {getSaleType(sale)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    No sales data available
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>
            
            <Box className="sales-history-footer">
                <Button 
                    variant="text"
                    onClick={handleViewFullHistory}
                    endIcon={<ArrowForwardIcon />}
                    className="view-full-history-button"
                >
                    View Full Sale History
                </Button>
            </Box>
        </Popover>
    );
};

SalesHistoryModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    anchorEl: PropTypes.object,
    salesList: PropTypes.array,
    propertyId: PropTypes.string.isRequired,
    addressState: PropTypes.string,
    undisclosedSalesAccess: PropTypes.bool,
    undisclosedSalesFeature: PropTypes.bool,
    getTransactionType: PropTypes.func,
    page: PropTypes.string,
    searchType: PropTypes.string,
    searchTab: PropTypes.string,
    resultView: PropTypes.string,
};

export default SalesHistoryModal;
